export const isS3Key = (key) =>
  key && typeof key === "string" && key.startsWith("samples/");

export const getImageKey = (img) => img?.key || img?.url || img?.preview;

export const mapSampleImagesToManagedImages = (sampleImages = []) =>
  sampleImages.map((img) => ({
    url: img.url || img,
    key: img.key || img,
    preview: img.url || img,
  }));

const normalizeImages = (images = []) =>
  images
    .map((img) => ({
      key: getImageKey(img),
      hasFile: Boolean(img.file),
    }))
    .sort((a, b) => (a.key || "").localeCompare(b.key || ""));

const getRemovedImageKeys = ({ previousImages = [], nextImages = [] }) => {
  const nextKeys = new Set(nextImages.map(getImageKey).filter(Boolean));

  return previousImages
    .map(getImageKey)
    .filter((key) => key && !nextKeys.has(key));
};

export const getImagesToSubmit = (currentImages = []) => {
  const newImages = currentImages
    .filter((img) => img.file && img.preview && !img.url)
    .map((img) => ({
      preview: img.preview,
      file: img.file,
    }));

  const keyedImages = currentImages
    .filter((img) => img.key && !img.file)
    .map((img) => ({ key: img.key }));

  return [...keyedImages, ...newImages];
};

export const createImageState = () => ({
  isEditing: false,
  initialImages: [],
  currentImages: [],
  deletedImageKeys: [],
});

export const sampleImageStateReducer = (state, action) => {
  switch (action.type) {
    case "START_EDIT": {
      const baseImages = mapSampleImagesToManagedImages(
        action.sampleImages || [],
      );
      return {
        isEditing: true,
        initialImages: baseImages,
        currentImages: baseImages,
        deletedImageKeys: [],
      };
    }

    case "SET_IMAGES": {
      const nextImages = action.images || [];
      const removedImageKeys = getRemovedImageKeys({
        previousImages: state.currentImages,
        nextImages,
      });

      return {
        ...state,
        currentImages: nextImages,
        deletedImageKeys: Array.from(
          new Set([...state.deletedImageKeys, ...removedImageKeys]),
        ),
      };
    }

    case "RESET":
      return createImageState();

    default:
      return state;
  }
};

export const hasImageChanges = (state) => {
  const before = normalizeImages(state.initialImages);
  const after = normalizeImages(state.currentImages);

  if (state.deletedImageKeys.length > 0) {
    return true;
  }

  if (before.length !== after.length) {
    return true;
  }

  return before.some(
    (img, index) =>
      img.key !== after[index].key || img.hasFile !== after[index].hasFile,
  );
};

export const getCancelPayload = (state) => {
  const initialKeys = state.initialImages.map((img) => img.key).filter(Boolean);
  const initialKeySet = new Set(initialKeys);

  // On cancel, cleanup:
  // 1. Transient deletes (images added then removed during edit)
  // 2. New staging images from mobile/desktop that need to be removed
  const transientDeletes = state.deletedImageKeys.filter(
    (key) => isS3Key(key) && !initialKeySet.has(key),
  );

  // Find staging images in current state that weren't in initial state (mobile uploads, new desktop uploads)
  const currentKeys = state.currentImages.map(getImageKey).filter(Boolean);
  const newStagingImages = currentKeys.filter(
    (key) =>
      isS3Key(key) &&
      !initialKeySet.has(key) &&
      (key.includes("/STAGING/") ||
        !state.initialImages.some((img) => getImageKey(img) === key)),
  );

  const allDeleteKeys = Array.from(
    new Set([...transientDeletes, ...newStagingImages]),
  );

  if (allDeleteKeys.length === 0) {
    return null;
  }

  return {
    sampleImages: initialKeys.map((key) => ({ key })),
    currentImageCount: initialKeys.length,
    deletedImageKeys: allDeleteKeys,
  };
};

export const getSavePayload = (state) => ({
  sampleImages: getImagesToSubmit(state.currentImages),
  currentImageCount: state.currentImages.length,
  deletedImageKeys: state.deletedImageKeys,
});
